import { useState } from 'react'
import './App.css'
import NewsSection from './NewsSection'
import SEO from './components/SEO'
import ShopSection from './components/ShopSection'

function App() {
  const [activeTab, setActiveTab] = useState('home')

  // Navigation items
  const navItems = [
    { id: 'portals', label: 'Global Portals' },
    { id: 'tourism', label: 'Yacht Tours' },
    { id: 'sales', label: 'Yacht Sales' },
    { id: 'charter', label: 'Yacht Charter' },
    { id: 'dealers', label: 'Dealers' },
    { id: 'repair', label: 'Repair Services' },
    { id: 'marinas', label: 'Marinas' },
    { id: 'resources', label: 'Resources' }
  ]

  // Global Yacht Portals data
  const globalPortals = [
    { name: 'YachtWorld', url: 'https://www.yachtworld.com/', description: 'World\'s largest yacht sales and charter market, thousands of vessels' },
    { name: 'Boat International', url: 'https://www.boatinternational.com/', description: 'Luxury yacht guide, specifications, news, directory' },
    { name: 'Yachting Pages', url: 'https://www.yachting-pages.com/', description: 'Superyacht services directory, repair, marinas, brokerage' },
    { name: 'Nautical Directory', url: 'https://nauticaldirectory.com/', description: 'Global yacht industry directory, shipyards, brokers, marinas' },
    { name: 'Superyachts.com', url: 'https://www.superyachts.com/', description: 'Superyacht directory, brokerage, marinas' }
  ]

  // Yacht Tours data
  const yachtTours = [
    { name: 'YachtCharterFleet', url: 'https://www.yachtcharterfleet.com/', description: 'World\'s largest luxury yacht charter comparison platform, all destinations' },
    { name: 'Dream Yacht Charter', url: 'https://www.dreamyachtcharter.com/', description: '20+ global destinations, sailing/powerboat vacation charters' },
    { name: 'The Moorings', url: 'https://www.moorings.com/', description: 'Top sailing destinations worldwide, sailing/powerboat charters' },
    { name: 'Sunsail', url: 'https://www.sunsail.com/', description: 'Global sailing vacation charters, 20+ destinations' },
    { name: 'Boatbookings', url: 'https://www.boatbookings.com/', description: 'Global luxury yacht charter experts' },
    { name: 'International Yacht Charter Group', url: 'https://www.internationalyachtchartergroup.com/', description: 'Private crewed luxury charters, global hotspots' }
  ]

  return (
    <>
      <SEO 
        title="Yacht Alpha - Global Yacht Navigation & Charter Services"
        description="Professional global yacht services navigation platform. Premium yacht charters, maritime services, and luxury boating experiences worldwide."
        keywords="yacht, luxury yachts, marine, sailing, boat charter, maritime,游艇,豪华游艇,航海,租船,海事"
        type="website"
      />
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-navy-900 text-white py-6">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold mb-2">🌍 Yacht Alpha - Global Yacht Navigation Website</h1>
          <p className="text-gray-300">Professional global yacht services navigation platform</p>
          <nav className="flex flex-wrap gap-3 mt-6">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="text-center py-12 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl mb-12">
          <h2 className="text-4xl font-bold text-navy-800 mb-4">Global Yacht Services Navigation</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Comprehensive navigation platform for yacht services worldwide, covering global portals, tours, sales, charters, repair, marinas and industry resources.
          </p>
          <div className="bg-red-500 text-white p-6 rounded-xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-3">🎉 Special Promotion!</h3>
            <p className="text-lg mb-2"><strong>Global Yacht Charter - 20% OFF!</strong></p>
            <p className="mb-2">Get 20% discount on all global yacht charters. Limited time offer!</p>
            <p><strong>Contact:</strong> info@yachtalpha.com</p>
          </div>
        <ShopSection />
        </section>

        {/* Global Yacht Portals */}
        <section id="portals" className="mb-12 bg-white p-8 rounded-2xl shadow-lg">
          <div className="border-b-2 border-navy-600 pb-4 mb-8">
            <h2 className="text-3xl font-bold text-navy-700">Global Yacht Portals / Directory Websites</h2>
            <p className="text-gray-600 mt-2">Large navigation/directory platforms covering sales, charters, news, directories - one-stop global information</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {globalPortals.map((portal) => (
              <div key={portal.name} className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-xl font-bold text-navy-700 mb-3">
                  <a href={portal.url} target="_blank" rel="noopener noreferrer" className="hover:text-navy-900 hover:underline">
                    {portal.name}
                  </a>
                </h3>
                <p className="text-gray-700 mb-4">{portal.description}</p>
                <span className="inline-block bg-blue-100 text-navy-700 px-3 py-1 rounded text-sm">
                  {new URL(portal.url).hostname}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Yacht Tours */}
        <section id="tourism" className="mb-12 bg-white p-8 rounded-2xl shadow-lg">
          <div className="border-b-2 border-navy-600 pb-4 mb-8">
            <h2 className="text-3xl font-bold text-navy-700">1. Yacht Tours / Sea Activities</h2>
            <p className="text-gray-600 mt-2">Short-term/day charters, parties, customized sea trips, yacht tours (including crewed yacht short-term charters)</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {yachtTours.map((tour) => (
              <div key={tour.name} className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-xl font-bold text-navy-700 mb-3">
                  <a href={tour.url} target="_blank" rel="noopener noreferrer" className="hover:text-navy-900 hover:underline">
                    {tour.name}
                  </a>
                </h3>
                <p className="text-gray-700 mb-4">{tour.description}</p>
                <span className="inline-block bg-blue-100 text-navy-700 px-3 py-1 rounded text-sm">
                  {new URL(tour.url).hostname}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* News Section */}
        <NewsSection 
          website="yachtalpha"
          limit={10000}
          title="Yachting Industry News"
          themeColor="blue"
        />

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12 mt-12 rounded-2xl">
          <div className="container mx-auto px-4 text-center">
            <h3 className="text-2xl font-bold mb-4">Yacht Alpha - Global Yacht Navigation</h3>
            <p className="text-gray-400 mb-6">Professional global yacht services navigation platform</p>
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="text-gray-300 hover:text-white transition-colors"
                  onClick={(e) => {
                    e.preventDefault()
                    document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })
                  }}
                >
                  {item.label}
                </a>
              ))}
            </div>
            <p className="text-gray-500">
              Contact: <a href="mailto:info@yachtalpha.com" className="text-blue-400 hover:text-blue-300">info@yachtalpha.com</a>
            </p>
            <p className="text-gray-500 mt-4">© {new Date().getFullYear()} Yacht Alpha. All rights reserved.</p>
          </div>
        </footer>
      </main>
    </div>
    </>
  )
}

export default App